import { GearSix, X } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FilterButton from "common/components/ui/FilterButton";
import FormField from "common/components/ui/FormField";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
} from "common/components/ui/Select";
import Switch from "common/components/ui/Switch";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  CrmIntegrationSettings as CrmIntegrationSettingsType,
  InstalledIntegrationSettingsItem,
} from "common/types";

import { INTEGRATIONS_LIST } from "../constants";
import useIntegrationMutations from "../datahooks/useIntegrationMutations";
import RemoveIntegration from "./RemoveIntegration";

const options = [
  { value: "new_connection", label: "All leads" },
  { value: "accepted", label: "Aimfox campaign leads" },
] as const;

interface CrmIntegrationSettingsProps {
  settings: InstalledIntegrationSettingsItem;
  integrationKey: keyof CrmIntegrationSettingsType;
}

export default function CrmIntegrationSettings({
  settings,
  integrationKey,
}: CrmIntegrationSettingsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const [currentSettings, setCurrentSettings] = useState(settings);

  const { updateIntegration, isUpdatingIntegration } =
    useIntegrationMutations();

  function onSave() {
    updateIntegration({ updates: currentSettings, integrationKey }).then(() => {
      setIsOpen(false);
    });
  }

  const Component = isDesktop ? Popover : Drawer;
  const Trigger = isDesktop ? PopoverTrigger : DrawerTrigger;
  const Content = isDesktop ? PopoverContent : DrawerContent;
  const Close = isDesktop ? PopoverClose : DrawerClose;

  const integrationName = INTEGRATIONS_LIST.find(
    (integration) => "key" in integration && integration.key === integrationKey,
  ).title;
  const title = `${integrationName} settings`;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen}>
      <Trigger asChild>
        <Button variant="secondary-black" intent="iconOnly" size="sm">
          <GearSix />
        </Button>
      </Trigger>

      <Content className="lg:w-80" align="start">
        {isDesktop ? (
          <div className="lg:p-4 flex items-center border-b border-b-black-200 justify-between">
            <h3 className="text-md-bold">{title}</h3>
            <Close>
              <X className="text-black-500 size-5" />
            </Close>
          </div>
        ) : (
          <DrawerTitle className="mb-8">{title}</DrawerTitle>
        )}
        <div className="flex flex-col lg:p-4 gap-6">
          <div className="bg-black-50 rounded-2xl p-2 flex gap-x-2.5">
            <div className="flex flex-col gap-y-0.5">
              <span className="text-sm-bold">Automatic sync</span>
              <p className="text-black-500 text-xs-medium">
                Automatically send Aimfox lead data to HubSpot
              </p>
            </div>
            <Switch
              checked={currentSettings.active}
              onCheckedChange={() => {
                setCurrentSettings({
                  ...currentSettings,
                  active: !currentSettings.active,
                });
              }}
            />
          </div>
          <FormField label="Which leads to sync" htmlFor="" size="lg">
            <Select
              value={currentSettings.events[0]}
              onValueChange={(value) =>
                setCurrentSettings({
                  ...currentSettings,
                  events: [value as (typeof options)[number]["value"]],
                })
              }
            >
              <SelectTrigger asChild>
                <FilterButton size="md">
                  {
                    options.find(
                      ({ value }) => value === currentSettings.events[0],
                    ).label
                  }
                </FilterButton>
              </SelectTrigger>
              <SelectContent>
                {options.map(({ value, label }) => (
                  <SelectItem value={value} key={value}>
                    <SelectItemText>{label}</SelectItemText>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <RemoveIntegration type={integrationKey} />
        </div>

        <div className="flex flex-col lg:flex-row-reverse lg:p-4 gap-2 lg:gap-2.5">
          <Button
            size={isDesktop ? "sm" : "lg"}
            className="flex-1"
            isLoading={isUpdatingIntegration}
            onClick={onSave}
          >
            Save
          </Button>
          <Close asChild>
            <Button
              variant="secondary-black"
              size={isDesktop ? "sm" : "lg"}
              className="flex-1"
              disabled={isUpdatingIntegration}
            >
              Cancel
            </Button>
          </Close>
        </div>
      </Content>
    </Component>
  );
}
